import { Btn } from '@vm/shared';

export interface Bindings {
  forward: string[];
  back: string[];
  left: string[];
  right: string[];
  jump: string[];
  reload: string[];
  gadget: string[];
  use: string[];
  lastWeapon: string[];
  scoreboard: string[];
  chat: string[];
  slots: string[][];
}

/** Rebindable actions (primary key per action; defaults keep their secondary keys). */
export type BindAction =
  | 'forward' | 'back' | 'left' | 'right' | 'jump' | 'reload' | 'gadget' | 'use' | 'lastWeapon' | 'scoreboard' | 'chat'
  | 'slot1' | 'slot2' | 'slot3' | 'slot4' | 'slot5' | 'slot6';

export const BIND_ACTIONS: { action: BindAction; label: string }[] = [
  { action: 'forward', label: 'Move forward' },
  { action: 'back', label: 'Move back' },
  { action: 'left', label: 'Strafe left' },
  { action: 'right', label: 'Strafe right' },
  { action: 'jump', label: 'Jump / jetpack' },
  { action: 'reload', label: 'Reload' },
  { action: 'gadget', label: 'Gadget' },
  { action: 'use', label: 'Use / pick up' },
  { action: 'lastWeapon', label: 'Last weapon' },
  { action: 'slot1', label: 'Primary' },
  { action: 'slot2', label: 'Backup' },
  { action: 'slot3', label: 'Melee' },
  { action: 'slot4', label: 'Heavy' },
  { action: 'slot5', label: 'Sniper' },
  { action: 'slot6', label: 'Build tool' },
  { action: 'scoreboard', label: 'Scoreboard' },
  { action: 'chat', label: 'Chat' },
];

export const DEFAULT_BINDINGS: Bindings = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  reload: ['KeyR'],
  gadget: ['KeyG', 'KeyF'],
  use: ['KeyE'],
  lastWeapon: ['KeyQ'],
  scoreboard: ['Tab'],
  chat: ['KeyT', 'Enter'],
  slots: [['Digit1'], ['Digit2'], ['Digit3'], ['Digit4'], ['Digit5'], ['Digit6', 'KeyB']],
};

/** Codes currently bound to an action. */
export function actionCodes(b: Bindings, action: BindAction): string[] {
  if (action.startsWith('slot')) return b.slots[Number(action.slice(4)) - 1];
  return b[action as Exclude<BindAction, `slot${number}`>];
}

/**
 * Applies user overrides: each override becomes the action's only key, and that key is removed
 * from every other action so one key never does two things.
 */
export function resolveBindings(overrides: Partial<Record<BindAction, string>> | undefined): Bindings {
  const b: Bindings = JSON.parse(JSON.stringify(DEFAULT_BINDINGS));
  if (!overrides) return b;
  const all = BIND_ACTIONS.map((a) => a.action);
  for (const [action, code] of Object.entries(overrides) as [BindAction, string][]) {
    if (!code || !all.includes(action)) continue;
    for (const other of all) {
      const list = actionCodes(b, other);
      const i = list.indexOf(code);
      if (i >= 0) list.splice(i, 1);
    }
    const list = actionCodes(b, action);
    list.splice(0, list.length, code);
  }
  return b;
}

/**
 * Collects raw input from keyboard, mouse (pointer lock), gamepad and the touch overlay.
 * The game samples it once per 60 Hz frame.
 */
export class Input {
  readonly keys = new Set<string>();
  /** Accumulated look delta in pixels since the last sample. */
  lookX = 0;
  lookY = 0;
  mouseButtons = 0;
  wheel = 0;
  /** Touch/gamepad analog movement (-1..1) and extra buttons. */
  touchMoveX = 0;
  touchMoveZ = 0;
  touchButtons = 0;
  /** Slot requested by key/wheel/touch; -1 = unchanged. */
  slotRequest = -1;
  lastWeaponRequest = false;
  onScoreboard?: (open: boolean) => void;
  onChat?: () => void;
  onMenu?: () => void;
  enabled = true;
  bindings: Bindings = DEFAULT_BINDINGS;
  private gamepadPrev = 0;
  private cleanup: (() => void)[] = [];

  constructor(private canvas: HTMLElement) {
    const on = <K extends keyof WindowEventMap>(t: K, fn: (e: WindowEventMap[K]) => void, target: Window | HTMLElement | Document = window): void => {
      target.addEventListener(t, fn as EventListener, { passive: false });
      this.cleanup.push(() => target.removeEventListener(t, fn as EventListener));
    };
    on('keydown', (e) => this.keyDown(e));
    on('keyup', (e) => {
      this.keys.delete(e.code);
      if (this.bindings.scoreboard.includes(e.code)) this.onScoreboard?.(false);
    });
    on('blur', () => {
      this.keys.clear();
      this.mouseButtons = 0;
    });
    on('mousemove', (e) => {
      if (document.pointerLockElement === this.canvas) {
        this.lookX += e.movementX;
        this.lookY += e.movementY;
      }
    });
    on('mousedown', (e) => {
      if (document.pointerLockElement !== this.canvas) return;
      this.mouseButtons |= 1 << e.button;
      e.preventDefault();
    });
    on('mouseup', (e) => {
      this.mouseButtons &= ~(1 << e.button);
    });
    on('wheel', (e) => {
      if (document.pointerLockElement === this.canvas) this.wheel += Math.sign(e.deltaY);
    });
    on('contextmenu', (e) => e.preventDefault(), canvas);
  }

  private keyDown(e: KeyboardEvent): void {
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    if (e.code === 'Escape') {
      this.onMenu?.();
      return;
    }
    if (!this.enabled) return;
    const b = this.bindings;
    if (b.scoreboard.includes(e.code)) {
      e.preventDefault();
      this.onScoreboard?.(true);
    }
    if (b.chat.includes(e.code) && !e.repeat) {
      e.preventDefault();
      this.onChat?.();
      return;
    }
    b.slots.forEach((codes, i) => {
      if (codes.includes(e.code)) this.slotRequest = i;
    });
    if (b.lastWeapon.includes(e.code) && !e.repeat) this.lastWeaponRequest = true;
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
    this.keys.add(e.code);
  }

  requestPointerLock(): void {
    if (document.pointerLockElement !== this.canvas) {
      const p = this.canvas.requestPointerLock?.() as unknown as Promise<void> | undefined;
      p?.catch?.(() => {});
    }
  }

  get locked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  private held(codes: string[]): boolean {
    for (const c of codes) if (this.keys.has(c)) return true;
    return false;
  }

  /** Samples movement axes and button bits for one frame. */
  sample(): { mx: number; mz: number; buttons: number } {
    let mx = 0;
    let mz = 0;
    let buttons = 0;
    if (this.enabled) {
      const b = this.bindings;
      if (this.held(b.forward)) mz += 1;
      if (this.held(b.back)) mz -= 1;
      if (this.held(b.right)) mx += 1;
      if (this.held(b.left)) mx -= 1;
      if (this.held(b.jump)) buttons |= Btn.Jump | Btn.Jet;
      if (this.held(b.reload)) buttons |= Btn.Reload;
      if (this.held(b.gadget)) buttons |= Btn.Gadget;
      if (this.held(b.use)) buttons |= Btn.Use;
      if (this.mouseButtons & 1) buttons |= Btn.Fire;
      if (this.mouseButtons & 4) buttons |= Btn.Aim;
      mx += this.touchMoveX;
      mz += this.touchMoveZ;
      buttons |= this.touchButtons;
      this.pollGamepad((gx, gz, gb) => {
        mx += gx;
        mz += gz;
        buttons |= gb;
      });
    }
    const len = Math.hypot(mx, mz);
    if (len > 1) {
      mx /= len;
      mz /= len;
    }
    return { mx, mz, buttons };
  }

  private pollGamepad(cb: (mx: number, mz: number, buttons: number) => void): void {
    const pads = navigator.getGamepads?.();
    if (!pads) return;
    for (const gp of pads) {
      if (!gp || !gp.connected) continue;
      const dz = (v: number): number => (Math.abs(v) < 0.18 ? 0 : v);
      const mx = dz(gp.axes[0] ?? 0);
      const mz = -dz(gp.axes[1] ?? 0);
      this.lookX += dz(gp.axes[2] ?? 0) * 14;
      this.lookY += dz(gp.axes[3] ?? 0) * 10;
      const pressed = (i: number): boolean => !!gp.buttons[i]?.pressed;
      let b = 0;
      if (pressed(7)) b |= Btn.Fire;
      if (pressed(6)) b |= Btn.Aim;
      if (pressed(0)) b |= Btn.Jump | Btn.Jet;
      if (pressed(2)) b |= Btn.Reload;
      if (pressed(5) || pressed(1)) b |= Btn.Gadget;
      const bits = gp.buttons.reduce((acc, bt, i) => acc | (bt.pressed ? 1 << i : 0), 0);
      const edge = bits & ~this.gamepadPrev;
      this.gamepadPrev = bits;
      if (edge & (1 << 3)) this.wheel += 1;
      if (edge & (1 << 4)) this.wheel -= 1;
      if (edge & (1 << 12)) this.slotRequest = 2;
      cb(mx, mz, b);
      return;
    }
  }

  takeLook(): [number, number] {
    const l: [number, number] = [this.lookX, this.lookY];
    this.lookX = 0;
    this.lookY = 0;
    return l;
  }

  dispose(): void {
    for (const c of this.cleanup) c();
  }
}
